import type { ReactElement } from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ChatAttachment, ChatContext, ProposalBuildState } from '@shared/api';
import { proposalBuildSimulationTimeline } from '@shared/buildSimulation';
import type { Decision, DocumentSection } from '@shared/types';
import { useShellState } from '../components/AppShell';
import { GridWorkspace } from '../components/GridWorkspace';
import { DownloadIcon, PlayFilledIcon } from '../components/icons';
import { ProposalGallery } from '../components/ProposalGallery';
import { BriefPreparationView } from '../components/build/BriefPreparationView';
import { AgentBar, emptyAgentBarState, type AgentBarAskState, type AgentBarHandle, type AgentBarState } from '../components/dock/ComposerDock';
import { DetailsNeededPanel } from '../components/dock/DetailsNeededPanel';
import { LiveBuildView } from '../components/build/LiveBuildView';
import { ScrollText } from '../components/build/ScrollText';
import { SimulationStrip } from '../components/build/SimulationStrip';
import { StageTracker, startingStageTrackerState } from '../components/build/StageTracker';
import { ShareMenu } from '../components/ShareMenu';
import { VersionsMenu } from '../components/VersionsMenu';
import type { PageActions } from '../pages/types';
import { deriveBuildingSectionIds, isBuildInFlight } from '../lib/buildRail';
import { briefSummaryLine } from '../lib/briefLine';
import { money } from '../lib/format';
import { themeStyle } from '../lib/theme';
import { workspaceHeaderCollapseState } from '../lib/workspaceHeaderCollapse';
import { resolvePageRef, tacticGroupDisplayTitle, visibleSections } from '../lib/plan';
import {
  diffProposalSections,
  hasProposalGridChanges,
  type ProposalGridChangeSet,
} from '../lib/proposalGridState';
import { decisionChatFollowUp } from '../lib/decisions';
import { openReviewCounts, openReviewItemsForPage } from '../lib/reviewItems';
import { approveProposalBuild, exportPdf } from '../api/client';
import { navigate } from '../state/router';
import { useContentLibrary } from '../state/ContentLibraryContext';
import { takeFirstMessage } from '../state/pendingMessage';
import { usePendingPages } from '../state/pendingPages';
import { useChat } from '../state/useChat';
import { useEmergence } from '../state/useEmergence';
import { useProposal } from '../state/useProposal';
import { useProposalBuild } from '../state/useProposalBuild';
import { useSelectionContext } from '../state/useSelectionContext';
import type { TaskRow } from '../state/taskRows';

/** Grid and gallery are complementary editing surfaces. */
type ViewMode = 'grid' | 'gallery';

function relativeUpdatedAt(value: string): string {
  const minutes = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 60_000));
  if (minutes < 1) return 'edited just now';
  if (minutes < 60) return `edited ${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return hours < 24 ? `edited ${hours}h ago` : `edited ${Math.floor(hours / 24)}d ago`;
}

/** Topbar subtitle during a build phase (docs/live-build.md, Integration rules). */
function buildSubtitle(state: ProposalBuildState): string | null {
  const total = state.pages.length;
  if (total === 0) return null;
  if (state.phase === 'ready') return `${total} pages · edited just now`;
  if (state.phase === 'building' || state.phase === 'needs_review' || state.phase === 'failed') {
    // A page counts once its content exists (docs/live-build.md §31), not
    // only after quality checking has passed it.
    const readyCount = state.pages.filter((page) =>
      page.status === 'content_ready' ||
      page.status === 'image_pending' ||
      page.status === 'checking' ||
      page.status === 'attention' ||
      page.status === 'ready',
    ).length;
    return `${readyCount} of ${total} pages`;
  }
  return null;
}

export function workflowStatusCopy(
  researching: boolean,
  state: ProposalBuildState | null,
  updatingPages: number,
): string {
  if (researching && state?.phase !== 'awaiting_confirmation') return 'Researching';
  if (updatingPages > 0) return `Updating ${updatingPages} page${updatingPages === 1 ? '' : 's'}`;
  if (!state) return 'Ready to build';
  if (state.phase === 'building') return 'Building draft';
  if (state.phase === 'failed') return 'Build paused';
  if (state.readiness.canSend) return 'Ready to send';
  if (state.phase === 'ready' || state.phase === 'needs_review') return `Draft ready · ${state.readiness.requiredDetailKeys.length} details still needed`;
  return state.readiness.canBuildDraft ? 'Ready to build' : 'Researching';
}

export type ProposalFlowSurface = 'research' | 'workspace_build' | 'workspace_grid';

/** The workspace milestone is sticky for this mounted proposal view: a later
 * correction chat cannot send the seller back to the research presentation. */
export function proposalFlowSurface({
  workspaceEntered,
  buildRequested,
  hasDocument,
  draftApprovedAt,
}: {
  workspaceEntered: boolean;
  buildRequested: boolean;
  hasDocument: boolean;
  phase: ProposalBuildState['phase'] | undefined;
  draftApprovedAt?: string;
}): ProposalFlowSurface {
  const entered = workspaceEntered || buildRequested || hasDocument || Boolean(draftApprovedAt);
  if (!entered) return 'research';
  if (hasDocument) return 'workspace_grid';
  return 'workspace_build';
}

/**
 * BUILDING → WORKSPACE for one proposal.
 *
 * Build and workspace states share one persistent two-column DOM: the agent
 * stays on the left while the proposal artifact stays on the right.
 */
export function ProposalView({ proposalId }: { proposalId: string }): ReactElement {
  const store = useProposal(proposalId);
  const lastChatVersion = useRef<number | null>(null);
  const syncChatVersion = useCallback((version: number) => {
    lastChatVersion.current = version;
    store.syncVersion(version);
  }, [store.syncVersion]);
  // docs/decisions.md §5e/§8b: a mid-turn `{kind:'decision'}` event carries no
  // version to compare against (unlike `state`/`done`), so the simplest
  // correct response is an unconditional refetch — the assumptions strip
  // reads straight off `proposal.pendingDecisions`.
  const onDecisionRaised = useCallback(() => {
    void store.refetch();
  }, [store.refetch]);
  // docs/custom-page.md "Pending page": placeholders live outside the chat
  // transcript so the grid and present mode can both read them regardless of
  // which surface is mounted. `documentSectionIds` drives the "swap once the
  // real page lands" rule (usePendingPages resolveAddedAgainstSections).
  const documentSectionIds = useMemo(
    () => store.response?.proposal.document?.sections.map((section) => section.id) ?? [],
    [store.response?.proposal.document?.sections],
  );
  const pendingPages = usePendingPages(documentSectionIds);
  const chat = useChat(
    proposalId,
    syncChatVersion,
    onDecisionRaised,
    pendingPages.onPagePending,
    pendingPages.onPageResolved,
    pendingPages.onChatSettled,
  );
  const build = useProposalBuild(proposalId);
  const [mode, setMode] = useState<ViewMode>('grid');
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [buildRequested, setBuildRequested] = useState(false);
  const [forceWorkspace, setForceWorkspace] = useState(false);
  const [latestChangeSet, setLatestChangeSet] = useState<ProposalGridChangeSet | null>(null);
  const [automaticHighlights, setAutomaticHighlights] = useState<string[]>([]);
  const [reviewHighlights, setReviewHighlights] = useState<string[]>([]);
  const [buildingAddedIds, setBuildingAddedIds] = useState<string[]>([]);
  const [pendingUpdateIds, setPendingUpdateIds] = useState<string[]>([]);
  const gridScrollTop = useRef(0);
  const gridRegionRef = useRef<HTMLDivElement | null>(null);
  const workspaceHeroRef = useRef<HTMLDivElement | null>(null);
  const workspaceHeroContentRef = useRef<HTMLDivElement | null>(null);
  const workspaceHeroTitleRef = useRef<HTMLButtonElement | null>(null);
  const workspaceChromeRef = useRef<HTMLDivElement | null>(null);
  const workspaceCompactTitleRef = useRef<HTMLButtonElement | null>(null);
  const workspaceCompactMetaRef = useRef<HTMLDivElement | null>(null);
  const collapseProgressRef = useRef(0);
  const reducedMotionRef = useRef(false);
  const [titleEditorLayer, setTitleEditorLayer] = useState<'hero' | 'compact'>('hero');
  const turnBase = useRef<{ version: number; sections: DocumentSection[] } | null>(null);
  const previousSending = useRef(false);
  const automaticHighlightTimer = useRef<number | null>(null);
  const reviewHighlightTimer = useRef<number | null>(null);
  const addedSlideTimer = useRef<number | null>(null);

  const { productName } = useContentLibrary();
  const composerRef = useRef<AgentBarHandle>(null);
  const buildComposerRef = useRef<AgentBarHandle>(null);
  // The bar moves between intake, build, grid, and gallery surfaces. Its UI
  // state belongs to this proposal view, rather than to any remounted shell.
  const [agentBarState, setAgentBarState] = useState<AgentBarState>(emptyAgentBarState);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [reviewTarget, setReviewTarget] = useState<{ itemId: string; pageId: string | null } | null>(null);
  const [workspaceEntered, setWorkspaceEntered] = useState(false);
  const openDetails = useCallback(() => {
    setDetailsOpen(true);
  }, []);
  const focusBuildComposer = useCallback((text: string) => {
    buildComposerRef.current?.setText(text);
  }, []);

  const onExportPdf = useCallback(async () => {
    setExporting(true);
    setExportError(null);
    try {
      const { blob, filename } = await exportPdf(proposalId);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : String(cause);
      setExportError(message);
      window.setTimeout(() => setExportError(null), 6000);
    } finally {
      setExporting(false);
    }
  }, [proposalId]);

  const proposal = store.response?.proposal ?? null;
  const proposalTitle =
    proposal?.opportunity.proposalName ?? proposal?.advertiser.name ?? 'New proposal';
  const hasDocument = (proposal?.document?.sections.length ?? 0) > 0;
  const ready = (store.response?.workspaceReady ?? false) && hasDocument;
  const { justEmerged } = useEmergence(proposalId, ready);
  const buildState = build.state;
  const simulatedTimeline = useMemo(
    () => buildState?.mode === 'simulated' ? proposalBuildSimulationTimeline(buildState) : [],
    [buildState?.proposalId, buildState?.runId, buildState?.mode, buildState?.sequence],
  );
  const [simulationStep, setSimulationStep] = useState(0);
  useEffect(() => { setSimulationStep(0); }, [buildState?.proposalId, buildState?.runId]);
  const displayedBuildState = buildState?.mode === 'simulated'
    ? simulatedTimeline[Math.min(simulationStep, Math.max(0, simulatedTimeline.length - 1))] ?? buildState
    : buildState;
  const reviewItems = displayedBuildState?.reviewItems ?? [];
  const openReviews = useMemo(() => openReviewCounts(reviewItems).open, [reviewItems]);
  // "Open current draft" (failed state) forces the normal workspace; the
  // override clears itself the moment the build leaves `failed` (a retry).
  useEffect(() => {
    if (displayedBuildState?.phase !== 'failed') setForceWorkspace(false);
  }, [displayedBuildState?.phase]);
  const workspaceMilestone = forceWorkspace || proposalFlowSurface({ workspaceEntered, buildRequested, hasDocument, phase: displayedBuildState?.phase, draftApprovedAt: displayedBuildState?.draftApprovedAt }) !== 'research';
  useEffect(() => {
    if (workspaceMilestone) setWorkspaceEntered(true);
  }, [workspaceMilestone]);
  // Once the brief is usable, later chat activity must not bounce the seller
  // back into the research presentation.
  const flowSurface = proposalFlowSurface({ workspaceEntered, buildRequested, hasDocument, phase: displayedBuildState?.phase, draftApprovedAt: displayedBuildState?.draftApprovedAt });
  const researchingIntake = flowSurface === 'research';
  const showIntake = researchingIntake;
  const showWorkspace = !showIntake;
  const showBuildExperience = flowSurface === 'workspace_build';
  const workspaceMeta = hasDocument
    ? `${proposal?.document?.sections.length ?? 0} pages · ${proposal ? relativeUpdatedAt(proposal.updatedAt) : ''} · v${proposal?.version ?? 0}`
    : workflowStatusCopy(researchingIntake, displayedBuildState, pendingUpdateIds.length);
  // G1/G2: the rail (StageTracker) and the per-page building skeletons both
  // key off this same "is a build actively landing copy" window — from the
  // rep's click through the last narrative page, not just `phase ===
  // 'building'` (web/src/lib/buildRail.ts has the full rationale).
  const buildInFlight = isBuildInFlight(displayedBuildState?.phase, buildRequested);
  const templateBuildingIds = useMemo(
    () => deriveBuildingSectionIds(proposal?.document?.sections ?? [], buildInFlight),
    [proposal?.document?.sections, buildInFlight],
  );
  // G4: the brief flash for a just-added slide (buildingAddedIds) stays a
  // separate, independent mechanism — this only ever unions more ids in.
  const buildingIds = useMemo(
    () => Array.from(new Set([...buildingAddedIds, ...templateBuildingIds])),
    [buildingAddedIds, templateBuildingIds],
  );

  useEffect(() => {
    const wasSending = previousSending.current;
    if (chat.sending && !wasSending && proposal?.document) {
      lastChatVersion.current = null;
      turnBase.current = {
        version: proposal.version,
        sections: proposal.document.sections,
      };
      setLatestChangeSet(null);
      setAutomaticHighlights([]);
      setReviewHighlights([]);
      setBuildingAddedIds([]);
    }
    if (!chat.sending && wasSending) {
      setPendingUpdateIds([]);
      if (buildRequested && !proposal?.document) setBuildRequested(false);
      if (lastChatVersion.current === null || (proposal && proposal.version >= lastChatVersion.current)) {
        turnBase.current = null;
      }
    }
    previousSending.current = chat.sending;
  }, [buildRequested, chat.sending, proposal]);

  useLayoutEffect(() => {
    const base = turnBase.current;
    if (!base || !proposal?.document || proposal.version <= base.version) return;
    const changeSet = diffProposalSections(base.sections, proposal.document.sections);
    if (!hasProposalGridChanges(changeSet)) return;

    setLatestChangeSet(changeSet);
    const affectedIds = [...changeSet.changedIds, ...changeSet.addedIds];
    setAutomaticHighlights(affectedIds);
    if (automaticHighlightTimer.current !== null) window.clearTimeout(automaticHighlightTimer.current);
    automaticHighlightTimer.current = window.setTimeout(() => setAutomaticHighlights([]), 1100);

    if (changeSet.addedIds.length > 0) {
      setBuildingAddedIds(changeSet.addedIds);
      if (addedSlideTimer.current !== null) window.clearTimeout(addedSlideTimer.current);
      addedSlideTimer.current = window.setTimeout(() => setBuildingAddedIds([]), 360);
    }
    if (!chat.sending && lastChatVersion.current !== null && proposal.version >= lastChatVersion.current) {
      turnBase.current = null;
    }
  }, [chat.sending, proposal?.version, proposal?.document]);

  useEffect(() => () => {
    if (automaticHighlightTimer.current !== null) window.clearTimeout(automaticHighlightTimer.current);
    if (reviewHighlightTimer.current !== null) window.clearTimeout(reviewHighlightTimer.current);
    if (addedSlideTimer.current !== null) window.clearTimeout(addedSlideTimer.current);
  }, []);

  const highlightedIds = useMemo(
    () => Array.from(new Set([...automaticHighlights, ...reviewHighlights])),
    [automaticHighlights, reviewHighlights],
  );

  useEffect(() => {
    if (build.state?.proposalVersion !== undefined) {
      store.syncVersion(build.state.proposalVersion);
    }
  }, [build.state?.proposalVersion, store.syncVersion]);

  useEffect(() => {
    if (!editingTitle) setTitleDraft(proposalTitle);
  }, [editingTitle, proposalTitle]);

  const selectionStore = useSelectionContext(showWorkspace && mode === 'gallery');
  const { selection } = selectionStore;

  // The message typed on the start screen, sent as soon as the proposal exists.
  const { send: chatSend } = chat;
  useEffect(() => {
    const first = takeFirstMessage(proposalId);
    if (first) chatSend(first.message, undefined, null, null, null, undefined, first.attachments);
  }, [proposalId, chatSend]);

  // Grid (nothing focused) sends no section id; the gallery feeds its active page.
  const activeSectionId = focusedId;

  const activeTitle = useMemo(() => {
    if (!activeSectionId || !proposal?.document) return null;
    const section = proposal.document.sections.find((candidate) => candidate.id === activeSectionId);
    if (!section) return null;
    return tacticGroupDisplayTitle(
      proposal,
      section.productIds ?? [],
      section.title,
      productName,
    );
  }, [activeSectionId, productName, proposal]);

  // A product context exists only while its slide is open. The grid itself is
  // navigation, not a persistent selection surface.
  const resolvedTacticId = useMemo(() => {
    if (mode === 'gallery' && focusedId && proposal?.document) {
      const section = proposal.document.sections.find((candidate) => candidate.id === focusedId);
      return section?.productId ?? null;
    }
    return null;
  }, [mode, focusedId, proposal]);

  const onOpenSlide = useCallback((sectionId: string) => {
    setReviewHighlights([]);
    setReviewTarget((current) => current?.pageId === sectionId ? current : null);
    setFocusedId(sectionId);
    setMode('gallery');
  }, []);

  const presentationStartId = proposal ? visibleSections(proposal)[0]?.id ?? null : null;
  const onStartPresentation = useCallback(() => {
    if (presentationStartId) onOpenSlide(presentationStartId);
  }, [onOpenSlide, presentationStartId]);

  const openPageReview = useCallback((pageId: string) => {
    const item = openReviewItemsForPage(reviewItems, pageId)[0];
    if (!item) return;
    setDetailsOpen(false);
    onOpenSlide(pageId);
    setReviewTarget({ itemId: item.id, pageId });
  }, [onOpenSlide, reviewItems]);

  const send = useCallback(
    (text: string, attachments: ChatAttachment[] = []) => {
      const context: ChatContext = {};
      if (activeSectionId) context.activeSectionId = activeSectionId;
      if (resolvedTacticId) context.activeTacticId = resolvedTacticId;
      if (selection) {
        context.selection = {
          sectionId: selection.sectionId,
          ...(selection.field ? { field: selection.field } : null),
          text: selection.text,
        };
      }
      const label = selection
        ? `Selection · ${selection.sectionTitle}`
        : activeTitle
          ? `Viewing · ${activeTitle}`
          : null;
      const targetSectionId = selection?.sectionId ?? activeSectionId;
      const targets = targetSectionId ? [targetSectionId] : [];
      setPendingUpdateIds(targets);
      // The page chip's send-time source (message-bar §2): the same section
      // the request targets, resolved to the grid's own number + title.
      const page = proposal ? resolvePageRef(proposal, targetSectionId, productName) : null;
      chatSend(text, Object.keys(context).length > 0 ? context : undefined, label, proposal?.version ?? null, page, undefined, attachments);
      selectionStore.clear();
    },
    [activeSectionId, activeTitle, chatSend, productName, proposal, resolvedTacticId, selection, selectionStore],
  );

  const buildProposal = useCallback(() => {
    if (buildRequested) return;
    setBuildRequested(true);
    void approveProposalBuild(proposalId)
      .then(() => send('Build the proposal now using the approved campaign brief.'))
      .catch(() => setBuildRequested(false));
  }, [buildRequested, proposalId, send]);

  const readiness = displayedBuildState?.readiness;
  const details = displayedBuildState?.details ?? [];
  const unresolvedDetails = details.filter((detail) => detail.status === 'missing');

  // LiveBuildView's page-level retry and the failed-build bar's "Retry
  // build" both funnel here: resend the last request rather than a canned
  // sentence (docs/composer-build-guide.md Step 8). The done row's own
  // Retry (Step 5) resends that exact turn's request instead.
  const retryLastRequest = useCallback(() => {
    const last = [...chat.messages].reverse().find((message) => message.role === 'user');
    if (last) send(last.text);
  }, [chat.messages, send]);

  // The done row's Undo restores the version at send, not one step back
  // (docs/composer-cards.md 5c) — a turn can save several versions (an
  // auto-fix, a tier regeneration) on top of the one instruction it
  // answered. Mirrors VersionsMenu's onRevert below; once it resolves the
  // row is marked undone so the button cannot fire twice.
  const { markUndone } = chat;
  const handleUndo = useCallback((row: TaskRow) => {
    const version = row.versionAtSend;
    if (version == null) return;
    void store.revert(version).then(() => markUndone(row.key, version));
  }, [store.revert, markUndone]);

  const { mutate } = store;

  // simplify.md §6 / decisions.md §7d: the assumptions-strip's Change —
  // resolves through /mutations (the SAME path the intake UI and the review
  // sheet use), never as a chat message, then — only when the decision
  // actually affects something authored content depends on — follows up
  // with ONE `[decision] …` message carrying the id so the server can attach
  // the §7a-shaped note (src/agent/decision-routing.ts's
  // noteForChatDecision) and the orchestrator re-authors just that.
  const onResolveDecision = useCallback(
    (decision: Decision, answerIndex: number) => {
      return mutate([{ op: 'resolve_decision', id: decision.id, answer: answerIndex }], 'Answered a decision').then(
        () => {
          const followUp = decisionChatFollowUp(decision, answerIndex);
          if (followUp) chatSend(followUp, undefined, null, proposal?.version ?? null, null, decision.id);
        },
      );
    },
    [mutate, chatSend, proposal],
  );

  const activeReviewItem = useMemo(() => {
    if (!reviewTarget) return null;
    const selected = openReviews.find((item) => item.id === reviewTarget.itemId);
    if (selected) return selected;
    return reviewTarget.pageId ? openReviewItemsForPage(reviewItems, reviewTarget.pageId)[0] ?? null : null;
  }, [openReviews, reviewItems, reviewTarget]);

  useEffect(() => {
    if (reviewTarget && !activeReviewItem) setReviewTarget(null);
  }, [activeReviewItem, reviewTarget]);

  const reviewAsk = useMemo<AgentBarAskState | undefined>(() => {
    if (!activeReviewItem || !proposal) return undefined;
    const pageId = reviewTarget?.pageId ?? activeReviewItem.pageIds[0] ?? null;
    const page = resolvePageRef(proposal, pageId, productName) ?? undefined;
    const decisionId = activeReviewItem.id.startsWith('decision:')
      ? activeReviewItem.id.slice('decision:'.length)
      : null;
    const decision = decisionId
      ? proposal.pendingDecisions.find((candidate) => candidate.id === decisionId && candidate.status === 'open')
      : undefined;

    if (decision) {
      return {
        card: {
          id: `contextual-review:${activeReviewItem.id}`,
          question: decision.question,
          answers: decision.options.map((option) => ({ label: option.label })),
          recommended: decision.assumed,
          allowLater: false,
          decisionId: decision.id,
        },
        lead: decision.why || activeReviewItem.detail,
        page,
        dismissible: false,
        onAnswer: (answer) => {
          const answerIndex = decision.options.findIndex((option) => option.label === answer);
          if (answerIndex >= 0) void onResolveDecision(decision, answerIndex);
        },
      };
    }

    return {
      card: {
        id: `contextual-review:${activeReviewItem.id}`,
        question: activeReviewItem.title,
        answers: [{ label: pageId ? 'Fix this page' : 'Ask agent to fix' }],
        recommended: 0,
        allowLater: false,
      },
      lead: activeReviewItem.detail,
      page,
      dismissible: false,
      onAnswer: () => send(
        activeReviewItem.detail
          ? `Fix this review item${pageId ? ' on the current page' : ''}: ${activeReviewItem.title}. ${activeReviewItem.detail}`
          : `Fix this review item${pageId ? ' on the current page' : ''}: ${activeReviewItem.title}.`,
      ),
    };
  }, [activeReviewItem, onResolveDecision, productName, proposal, reviewTarget?.pageId, send]);

  const openBlockingWork = useCallback(() => {
    const blockingReview = openReviews.find((item) => item.severity === 'required');
    if (!blockingReview) {
      openDetails();
      return;
    }
    const pageId = blockingReview.pageIds[0] ?? null;
    setDetailsOpen(false);
    if (pageId) onOpenSlide(pageId);
    setReviewTarget({ itemId: blockingReview.id, pageId });
  }, [onOpenSlide, openDetails, openReviews]);

  // decisions.md §2c / §8b: the assumptions-strip's × — the rep keeps the
  // assumption as carried and wants the row gone. Same /mutations path as
  // Change, but nothing is re-authored, so no `[decision] …` follow-up.
  const onDismissDecision = useCallback(
    (decision: Decision) => {
      void mutate([{ op: 'dismiss_decision', id: decision.id, by: 'rep' }], 'Dismissed an assumption');
    },
    [mutate],
  );

  const saveProposalTitle = useCallback(() => {
    setEditingTitle(false);
    const next = titleDraft.trim();
    if (!next || next === proposalTitle) {
      setTitleDraft(proposalTitle);
      return;
    }
    void mutate(
      [{ op: 'set_opportunity', patch: { proposalName: next, titleSource: 'user' } }],
      `Renamed proposal to ${next}`,
    );
  }, [mutate, proposalTitle, titleDraft]);

  const cancelProposalTitle = useCallback(() => {
    setTitleDraft(proposalTitle);
    setEditingTitle(false);
  }, [proposalTitle]);

  const beginTitleEdit = useCallback((requestedLayer?: 'hero' | 'compact') => {
    setTitleEditorLayer(requestedLayer ?? (collapseProgressRef.current < 0.5 ? 'hero' : 'compact'));
    setEditingTitle(true);
  }, []);

  const paintWorkspaceCollapse = useCallback((scrollTop: number) => {
    const hero = workspaceHeroRef.current;
    if (!hero) return;
    const values = workspaceHeaderCollapseState(scrollTop, hero.offsetHeight, reducedMotionRef.current);
    collapseProgressRef.current = values.progress;
    hero.style.transform = `translateY(${values.heroTranslateY}px)`;
    hero.style.opacity = String(values.heroOpacity);
    hero.style.filter = `blur(${values.heroBlur}px)`;
    if (workspaceChromeRef.current) {
      workspaceChromeRef.current.style.opacity = String(values.chromeOpacity);
      workspaceChromeRef.current.style.boxShadow = 'none';
    }
    if (workspaceCompactTitleRef.current) {
      workspaceCompactTitleRef.current.style.opacity = String(values.compactTitleOpacity);
      workspaceCompactTitleRef.current.style.transform = `translateY(${values.compactTitleTranslateY}px)`;
      workspaceCompactTitleRef.current.tabIndex = values.progress >= 0.5 ? 0 : -1;
      workspaceCompactTitleRef.current.setAttribute('aria-hidden', String(values.progress < 0.5));
    }
    if (workspaceHeroTitleRef.current) {
      workspaceHeroTitleRef.current.tabIndex = values.progress < 0.5 ? 0 : -1;
      workspaceHeroTitleRef.current.setAttribute('aria-hidden', String(values.progress >= 0.5));
    }
    if (workspaceCompactMetaRef.current) {
      workspaceCompactMetaRef.current.style.opacity = String(values.compactMetaOpacity);
      workspaceCompactMetaRef.current.style.transform = `translateY(${values.compactMetaTranslateY}px)`;
    }
  }, []);

  const rememberGridScroll = useCallback((scrollTop: number) => {
    gridScrollTop.current = scrollTop;
    paintWorkspaceCollapse(scrollTop);
  }, [paintWorkspaceCollapse]);

  const setGridRegion = useCallback((element: HTMLDivElement | null) => {
    gridRegionRef.current = element;
  }, []);

  useLayoutEffect(() => {
    if (!showWorkspace || !hasDocument) return undefined;
    const hero = workspaceHeroRef.current;
    if (!hero) return undefined;
    const measure = () => {
      const region = gridRegionRef.current;
      if (!region) return;
      region.style.paddingTop = `${hero.offsetHeight}px`;
      paintWorkspaceCollapse(region.scrollTop);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(hero);
    const fonts = document.fonts;
    void fonts?.ready.then(measure);
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onMotionChange = () => { reducedMotionRef.current = media.matches; measure(); };
    reducedMotionRef.current = media.matches;
    media.addEventListener('change', onMotionChange);
    return () => { observer.disconnect(); media.removeEventListener('change', onMotionChange); };
  }, [hasDocument, paintWorkspaceCollapse, proposalTitle, showWorkspace]);

  const actions: PageActions = useMemo(
    () => ({
      saveContent: (section, patch) => {
        void mutate(
          [{ op: 'set_section_content', sectionId: section.id, patch }],
          `Edited ${section.title} copy`,
        );
      },
      saveTitle: (section, title) => {
        void mutate(
          [{ op: 'set_section_content', sectionId: section.id, patch: {}, title }],
          `Renamed ${section.title} to ${title}`,
        );
      },
      setChannelBudget: (productId, monthlyBudget, label) => {
        void mutate(
          [{ op: 'set_channel_budget', productId, monthlyBudget }],
          `Changed ${label} budget to ${money(monthlyBudget) ?? monthlyBudget}`,
        );
      },
      setMonthlyBudget: (monthly) => {
        void mutate(
          [{ op: 'set_budget', monthly }],
          `Changed monthly budget to ${money(monthly) ?? monthly}`,
        );
      },
      selectTier: (tierId, label) => {
        void mutate([{ op: 'select_tier', tierId }], `Selected the ${label} plan`);
      },
      setHidden: (section, hidden) => {
        void mutate(
          [{ op: 'set_section_content', sectionId: section.id, patch: {}, hidden }],
          hidden ? `Hid ${section.title}` : `Restored ${section.title}`,
        );
      },
      setGeography: (patch, label) => {
        void mutate([{ op: 'set_geography', patch }], label);
      },
      setAudience: (patch, label) => {
        void mutate([{ op: 'set_audience', patch }], label);
      },
      setPriceOverride: (productId, retail, label) => {
        void mutate([{ op: 'set_price_override', productId, retail }], label);
      },
      clearPriceOverride: (productId, label) => {
        void mutate([{ op: 'clear_price_override', productId }], label);
      },
    }),
    [mutate],
  );

  useShellState({
    activeProposalId: proposalId,
    advertiserName: proposal ? proposalTitle : null,
    buildStatus: buildState?.status,
  });

  if (store.loading && !proposal) {
    return (
      <div className="shell">
        <DelayedLoading label="Opening proposal…" />
      </div>
    );
  }

  if (store.error && !proposal) {
    return (
      <div className="shell">
        <p className="loading">{store.error}</p>
        <button type="button" className="linkish" onClick={() => navigate('/')}>
          Start over
        </button>
      </div>
    );
  }

  if (!proposal || !store.response) {
    return (
      <div className="shell" />
    );
  }

  const stageClass = [
    'stage',
    'stage-workspace',
    showIntake ? 'stage-intake' : '',
    showBuildExperience ? 'stage-build' : '',
    showWorkspace && !hasDocument ? 'stage-workspace-prebuild' : '',
    justEmerged ? 'stage-emerging' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const stageStyle = undefined;

  return (
    <>
      <div className="shell">
        {!showWorkspace ? <header className="topbar">
          <div className={`topbar-title-block${showWorkspace ? ' topbar-title-block-workspace' : ''}`}>
            {editingTitle ? (
              <input
                className="topbar-title topbar-title-input"
                value={titleDraft}
                autoFocus
                onChange={(event) => setTitleDraft(event.target.value)}
                onBlur={saveProposalTitle}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') { event.preventDefault(); saveProposalTitle(); }
                  if (event.key === 'Escape') { event.preventDefault(); setTitleDraft(proposalTitle); setEditingTitle(false); }
                }}
                aria-label="Proposal title"
              />
            ) : (
              <button type="button" className="topbar-title topbar-title-button" onClick={() => setEditingTitle(true)} title="Rename proposal">
                {proposalTitle}
              </button>
            )}
            {showBuildExperience && displayedBuildState ? (
              <ScrollText as="p" className="topbar-subtitle">{buildSubtitle(displayedBuildState)}</ScrollText>
            ) : null}
          </div>
        </header> : null}

        {store.saveError ? (
          <div className="save-error" role="status">
            {store.saveError}
            <button type="button" className="linkish" onClick={store.clearSaveError}>
              Dismiss
            </button>
          </div>
        ) : null}

        <div className={stageClass} style={stageStyle}>
          <div className="stage-proposal">
            {showIntake ? <>
              <BriefPreparationView state={displayedBuildState} />
              <AgentBar
                ref={composerRef}
                mode="intake"
                messages={chat.messages}
                queue={chat.queue}
                decisions={proposal.pendingDecisions}
                onResolveDecision={onResolveDecision}
                onDismissDecision={onDismissDecision}
                onSend={send}
                onStop={chat.stop}
                onRemoveQueued={chat.removeQueued}
                onDismiss={chat.dismiss}
                onUndo={handleUndo}
                placeholder={chat.sending ? 'Add another instruction — it will queue…' : 'Add context or a correction…'}
                state={agentBarState}
                onStateChange={setAgentBarState}
                ask={displayedBuildState?.phase === 'awaiting_confirmation' && readiness?.canBuildDraft ? {
                  card: {
                    id: 'approve-brief-and-build',
                    question: briefSummaryLine(proposal),
                    answers: [{ label: 'Approve & build' }],
                    recommended: 0,
                    allowLater: false,
                  },
                  onAnswer: () => buildProposal(),
                  dismissible: false,
                  hidesReplies: true,
                } : undefined}
              />
            </> : <div className={`workspace-collapse-root${hasDocument ? '' : ' workspace-collapse-root--compact'}`} style={themeStyle(proposal.document?.theme)}>
              {hasDocument ? <div className="workspace-collapse-hero" ref={workspaceHeroRef}>
                <div className="workspace-collapse-hero-content" ref={workspaceHeroContentRef}>
                  {editingTitle && titleEditorLayer === 'hero' ? (
                    <input
                      className="workspace-hero-title workspace-title-input"
                      value={titleDraft}
                      autoFocus
                      onChange={(event) => setTitleDraft(event.target.value)}
                      onBlur={saveProposalTitle}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') { event.preventDefault(); saveProposalTitle(); }
                        if (event.key === 'Escape') { event.preventDefault(); cancelProposalTitle(); }
                      }}
                      aria-label="Proposal title"
                    />
                  ) : (
                    <button
                      type="button"
                      ref={workspaceHeroTitleRef}
                      className="workspace-hero-title workspace-title-button"
                      onClick={() => beginTitleEdit('hero')}
                      title="Rename proposal"
                      tabIndex={collapseProgressRef.current < 0.5 ? 0 : -1}
                      aria-hidden={collapseProgressRef.current >= 0.5}
                    >{proposalTitle}</button>
                  )}
                  <p className="workspace-hero-meta">{chat.sending && hasDocument ? 'Updating…' : workspaceMeta}</p>
                </div>
              </div> : null}
              <div className={hasDocument ? undefined : 'workspace-prebuild-region'}>
                {hasDocument ? <GridWorkspace
                proposal={proposal}
                updatingIds={chat.sending ? pendingUpdateIds : []}
                highlightedIds={highlightedIds}
                newlyAddedIds={latestChangeSet?.addedIds ?? []}
                buildingIds={buildingIds}
                pendingPages={pendingPages.entries}
                details={unresolvedDetails}
                updating={chat.sending}
                meta={workspaceMeta}
                initialScrollTop={gridScrollTop.current}
                onScrollPositionChange={rememberGridScroll}
                onRegionReady={setGridRegion}
                onFocus={onOpenSlide}
                reviewItems={reviewItems}
                onReview={openPageReview}
                stageTracker={buildInFlight ? (
                  <StageTracker
                    state={displayedBuildState?.phase === 'building' ? displayedBuildState : startingStageTrackerState()}
                    variant="compact"
                  />
                ) : null}
                /> : displayedBuildState ? <LiveBuildView state={displayedBuildState} proposal={proposal} buildRequested={buildRequested} onRetryBuild={retryLastRequest} onOpenDraft={() => setForceWorkspace(true)} onAttentionClick={(page) => openPageReview(page.id)} details={unresolvedDetails} /> : buildRequested ? <div className="live-build-view"><StageTracker state={startingStageTrackerState()} /></div> : <div className="workspace-build-pending">Starting the draft build…</div>}
              </div>
              <header className="workspace-collapse-header">
                <div className="workspace-collapse-chrome" ref={workspaceChromeRef} />
                <div className="workspace-collapse-header-copy">
                  {editingTitle && titleEditorLayer === 'compact' ? (
                    <input
                      className="workspace-compact-title workspace-title-input"
                      value={titleDraft}
                      autoFocus
                      onChange={(event) => setTitleDraft(event.target.value)}
                      onBlur={saveProposalTitle}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') { event.preventDefault(); saveProposalTitle(); }
                        if (event.key === 'Escape') { event.preventDefault(); cancelProposalTitle(); }
                      }}
                      aria-label="Proposal title"
                    />
                  ) : (
                    <button
                      type="button"
                      ref={workspaceCompactTitleRef}
                      className="workspace-compact-title workspace-title-button"
                      onClick={() => beginTitleEdit('compact')}
                      title="Rename proposal"
                      tabIndex={!hasDocument || collapseProgressRef.current >= 0.5 ? 0 : -1}
                      aria-hidden={hasDocument && collapseProgressRef.current < 0.5}
                    >{proposalTitle}</button>
                  )}
                  <span className="workspace-compact-meta" ref={workspaceCompactMetaRef}>{chat.sending && hasDocument ? 'Updating…' : workspaceMeta}</span>
                </div>
                <div className="workspace-action-segment workspace-collapse-actions" role="toolbar" aria-label="Proposal actions">
                  <VersionsMenu proposalId={proposalId} currentVersion={proposal.version} onUndo={() => void store.undo()} onRevert={(version) => void store.revert(version)} busy={store.saving} />
                  <button type="button" className="workspace-action-button" onClick={() => readiness?.canSend ? void onExportPdf() : openBlockingWork()} disabled={exporting} title={readiness?.canSend ? (exportError ?? 'Download the current version as a PDF') : 'Finish details before exporting'} aria-label="Export proposal PDF"><DownloadIcon /></button>
                  <ShareMenu proposalId={proposalId} />
                  <button type="button" className="workspace-present-button" onClick={onStartPresentation} disabled={!presentationStartId} title="Start presentation from slide 1">
                    <span className="workspace-present-badge" aria-hidden="true"><PlayFilledIcon /></span>
                    <span>Present</span>
                  </button>
                </div>
              </header>
              {mode === 'grid' ? (
                <AgentBar
                  ref={buildComposerRef}
                  messages={chat.messages}
                  queue={chat.queue}
                  decisions={proposal.pendingDecisions}
                  onResolveDecision={onResolveDecision}
                  onDismissDecision={onDismissDecision}
                  onSend={send}
                  onStop={chat.stop}
                  onRemoveQueued={chat.removeQueued}
                  onDismiss={chat.dismiss}
                  onUndo={handleUndo}
                  scopeActive={false}
                  state={agentBarState}
                  onStateChange={setAgentBarState}
                  panel={detailsOpen ? <DetailsNeededPanel details={unresolvedDetails} onPrefill={focusBuildComposer} onFocusPage={onOpenSlide} onClose={() => setDetailsOpen(false)} /> : undefined}
                  ask={reviewAsk}
                />
              ) : null}
              {mode === 'gallery' && focusedId ? (
                <ProposalGallery
                  proposal={proposal}
                  actions={actions}
                  initialSectionId={focusedId}
                  pendingPages={pendingPages.entries}
                  onActiveSectionChange={(sectionId) => {
                    setFocusedId(sectionId);
                    setReviewTarget((current) => current?.pageId === sectionId ? current : null);
                  }}
                  onClose={() => {
                    setFocusedId(null);
                    setReviewTarget(null);
                    setMode('grid');
                  }}
                  messageBar={(
                    <AgentBar
                      ref={buildComposerRef}
                      messages={chat.messages}
                      queue={chat.queue}
                      decisions={proposal.pendingDecisions}
                      onResolveDecision={onResolveDecision}
                      onDismissDecision={onDismissDecision}
                      onSend={send}
                      onStop={chat.stop}
                      onRemoveQueued={chat.removeQueued}
                      onDismiss={chat.dismiss}
                      onUndo={handleUndo}
                      scopeActive
                      state={agentBarState}
                      onStateChange={setAgentBarState}
                      panel={detailsOpen ? <DetailsNeededPanel details={unresolvedDetails} onPrefill={focusBuildComposer} onFocusPage={onOpenSlide} onClose={() => setDetailsOpen(false)} /> : undefined}
                      ask={reviewAsk}
                    />
                  )}
                />
              ) : null}
            </div>}
            {buildState?.mode === 'simulated' ? <SimulationStrip step={simulationStep} steps={simulatedTimeline.length} onStep={setSimulationStep} /> : null}
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Most proposals open in well under a second; saying "Opening…" for that
 * instant reads as a flash. Hold the line back until a load is actually slow.
 */
function DelayedLoading({ label }: { label: string }): ReactElement | null {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setVisible(true), 400);
    return () => window.clearTimeout(timer);
  }, []);
  return visible ? <p className="loading">{label}</p> : null;
}
